import type { Option } from "../core/option.ts";
import { TransactionLog } from "@tento-core/transaction-log";

import type { MutationController } from "./mutation-controller.ts";
import type { MutationAck } from "./pending-mutation.ts";
import type { GestureTxWriter, RollbackRecord } from "../tx/tx-log.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";

/**
 * One step of a {@link MutationTransaction}: the forward op plus the op that
 * undoes it once it has been submitted + confirmed (its compensation), or null
 * when the op needs no undo. The caller — which holds the pre-mutation state —
 * supplies the inverse, so the transaction stays generic and never inspects
 * `Op` (an `update`'s restore patch / a `delete`'s reinsert can only be built by
 * the domain that captured the prior value).
 */
export interface TxStep<Op> {
  readonly op: Op;
  readonly inverse: Option<Op>;
}

/**
 * A gesture to run through {@link MutationTransaction}: a `label` + a semantic
 * `intent` (the TX2 frame-commit stamp) plus the ordered steps. The `label`
 * names the WAL scope; the `intent` is the durable-horizon anchor later control
 * ops reference.
 */
export interface GestureRun<Op> {
  readonly label: string;
  readonly intent: string;
  readonly steps: readonly TxStep<Op>[];
}

/**
 * Thrown when a gesture's durable settlement fails and the confirmed steps have
 * been compensated. Carries the {@link RollbackRecord} (the same payload written
 * to the WAL as `txn/rollback`) so a caller can inspect the unwound horizon
 * without re-reading the log; the original forward failure is preserved as the
 * error `cause`.
 */
export class GestureRollbackError extends Error {
  readonly rollback: RollbackRecord;

  constructor(rollback: RollbackRecord, cause: Error) {
    super(
      `gesture rolled back after ${rollback.steps} durable step(s): ${rollback.errorTag}`,
      { cause },
    );
    this.name = "GestureRollbackError";
    this.rollback = rollback;
  }
}

/**
 * A saga over a {@link MutationDriver} — the WAL-journalled durable-horizon
 * unwinder (TXN-5 / ERR-1 MECHANISM). A gesture runs in two phases over two
 * horizons:
 *
 *  1. **Frame-commit** (synchronous): every step's optimistic effect is
 *     `beginOptimistic`-applied inside one intent-stamped `scopeIntent(label,
 *     intent)` scope, which seals synchronously as transaction `F`. `F` is the
 *     frame-commit anchor.
 *  2. **Durable-commit** (async): the step handles settle in order. Each ack
 *     appends `txn/durable-ack{ofTxId:F, step}`; a nack compensates the
 *     already-confirmed steps (their inverses, most-recent-first, best-effort),
 *     appends a typed `txn/rollback{ofTxId:F, …}`, and throws
 *     {@link GestureRollbackError}. A pre-durable failure (nothing confirmed)
 *     rolls back with `durable:false, steps:0` and submits no inverses.
 *
 * The frozen sealed `F` envelope is never mutated — `F`'s durable horizon is
 * DERIVED by folding the control ops. The overlay stays WAL-unaware: the
 * optimistic appends and the compensating inverse submits touch only the overlay;
 * the WAL is journalled separately through the {@link GestureTxWriter}. A null
 * `tx` makes the whole run fully dark — the optimistic + compensation behavior is
 * byte-identical to a bare `await` sequence, just without any WAL records.
 *
 * Generic over `Op`/`Item`. Compensation is best-effort: an inverse that itself
 * fails is swallowed, because the server is already partially applied and the
 * FORWARD error is the actionable one to surface.
 *
 * Adopted by the recurrence fork gestures (`EventsClient.updateOccurrence` /
 * `moveOccurrence` "this"/"following", via the `@tento/lona` `OccurrenceSagaDriver`
 * — a {@link MutationDriver} settling through internally-atomic client writes).
 * Drag-move sagas still need a DETACHED settle variant (frame-commit
 * synchronously, settle off the gesture's critical path) — see the 2026-06-12
 * detached-write / supersede / CAS learning; not built here.
 */
export class MutationTransaction<Op, Item> {
  readonly #tx: Option<GestureTxWriter>;
  readonly #controller: MutationDriver<Op, Item>;
  constructor(
    controller: MutationDriver<Op, Item>,
    tx: Option<GestureTxWriter>,
  ) {
    this.#controller = controller;
    this.#tx = tx;
  }

  /**
   * Run `gesture` against `rowKey`. Frame-commits all steps' optimistic effects
   * synchronously (one intent txn `F`), then settles them in order. On success
   * returns each step's ack. On the first durable failure, compensates the
   * already-confirmed steps in reverse, journals the rollback, and throws
   * {@link GestureRollbackError}.
   */
  async run(
    rowKey: RowNetworkKey,
    gesture: GestureRun<Op>,
  ): Promise<MutationAck<Item>[]> {
    const tx = this.#tx;
    const pending: Array<{
      readonly step: TxStep<Op>;
      readonly handle: MutationController.Handle<Op, Item>;
    }> = [];
    const begin = (): void => {
      for (const step of gesture.steps) {
        pending.push({ step, handle: this.#controller.beginOptimistic(rowKey, step.op) });
      }
    };

    // ── Phase 1: frame-commit ──────────────────────────────────────────────────
    // The intent scope seals synchronously in `scopeIntent`'s `finally`, so `F`
    // is `tau()` read immediately after — same-microtask, no await has run since
    // the seal, so the read is safe.
    let frameTxId = 0;
    if (tx != null) {
      tx.scopeIntent(gesture.label, gesture.intent, begin);
      frameTxId = tx.tau();
    } else {
      begin();
    }

    // ── Phase 2: durable-commit ────────────────────────────────────────────────
    const confirmed: TxStep<Op>[] = [];
    const acks: MutationAck<Item>[] = [];
    for (const [i, current] of pending.entries()) {
      let ack: MutationAck<Item>;
      try {
        ack = await current.handle.settle();
      } catch (err) {
        // Unwind: compensate the confirmed steps (inverses, reverse), and drop
        // every sibling begun in the frame-commit but not yet settled — the
        // failing handle already dropped itself; the trailing ones have not.
        await this.#compensate(rowKey, confirmed);
        for (const later of pending.slice(i + 1)) later.handle.drop();
        const rollback: RollbackRecord = {
          ofTxId: frameTxId,
          intent: gesture.intent,
          errorTag: TransactionLog.errorTag(err),
          steps: confirmed.length,
          durable: confirmed.length > 0,
        };
        tx?.recordRollback(rollback);
        throw new GestureRollbackError(
          rollback,
          err instanceof Error ? err : new Error(String(err)),
        );
      }
      confirmed.push(current.step);
      acks.push(ack);
      tx?.recordDurableAck(frameTxId, i);
    }
    return acks;
  }

  /**
   * Submit the inverse of each confirmed step, most-recent first. Best-effort: a
   * failing inverse is swallowed so the original (forward) error still surfaces.
   * Touches only the overlay (via `controller.perform`) — the WAL rollback is
   * journalled separately by the caller as one `txn/rollback` control op.
   */
  async #compensate(
    rowKey: RowNetworkKey,
    confirmed: readonly TxStep<Op>[],
  ): Promise<void> {
    for (const step of [...confirmed].reverse()) {
      const inverse = step.inverse;
      if (inverse == null) continue;
      try {
        await this.#controller.perform(rowKey, inverse);
      } catch {
        // Best-effort rollback; the forward error is the one we rethrow.
      }
    }
  }
}

/**
 * The minimal surface {@link MutationTransaction} drives: the begin/settle split
 * for the forward steps plus the one-shot `perform` compensation uses.
 * {@link MutationController} satisfies it as-is (the overlay-backed substrate
 * binding). A domain gesture driver may satisfy it instead when each of its
 * steps settles through a client method that is already internally optimistic
 * and self-rolling-back — there `beginOptimistic` is inert (the optimistic
 * effect happens inside `settle`) and `drop` is a no-op, which is exactly the
 * transaction's null-`tx` degenerate mode plus compensation. See
 * `OccurrenceSagaDriver` in `@tento/lona`.
 */
export interface MutationDriver<Op, Item> {
  beginOptimistic(rowKey: RowNetworkKey, op: Op): MutationController.Handle<Op, Item>;
  perform(rowKey: RowNetworkKey, op: Op): Promise<MutationAck<Item>>;
}
